/**
 * PostHog feature flag keys.
 *
 * Each value must match the flag key in PostHog exactly — a mismatch resolves to `undefined`,
 * which `variantToEnabled` treats as off, so the feature silently never ships with no error
 * anywhere. Read them through `isFeatureEnabled` / `useGetFlagData` from `@/utils/featureFlags`,
 * never `posthog.getFeatureFlag()` directly (see docs/FEATURE_FLAGS.md).
 *
 * Targeting and rollout live in each flag's PostHog release conditions, not here. URL-scoped
 * flags must target `$initial_current_url` — `$current_url` resolves false on the landing page.
 *
 * New keys use kebab-case.
 */

/** Settlement calculator on /thank-you. Domain targeting lives in the release conditions. See ClickUp 868ke0k1m. */
export const TYP_CALCULATOR_FLAG = 'typ-settlement-calculator';

/** CES web chat CTA on /thank-you. Multivariate: `control` reads as off via `variantToEnabled`. */
export const TYP_AI_CHAT_FLAG = 'typ-ai-chat';

/**
 * AI SMS CTA on /thank-you (ThankYouV2's TYPCTAs). Deliberately separate from
 * TYP_AI_CHAT_FLAG: the two options ship independently and web chat is expected first.
 */
export const TYP_AI_SMS_FLAG = 'typ-ai-sms';

/**
 * AI Call Now CTA on /thank-you (ThankYouV2's TYPCTAs): V2's call_now option, which dials the AI
 * phone agent rather than intake. Gated like the two above — the flag owns rollout, the TYP
 * call_now field owns per-contract availability. The line is hard-coded in TYPCTAs
 * (AI_CALL_NOW_NUMBER) until the TYP API serves it. Its own flag so it ships on its own schedule.
 */
export const TYP_AI_CALL_NOW_FLAG = 'typ-ai-call-now';

/**
 * Redesigned /thank-you (ThankYouV2) for sold internal leads: `sold === 1 && internal === 1` in
 * the TYP payload. useThankYouData reads it (or TYP_AI_REDESIGN_PARAM) once, at fetch time.
 * SingleForm reads it at redirect and forwards the decision as `?ai_redesign=true`. Boolean;
 * create it in PostHog before enabling anything.
 */
export const TYP_AI_REDESIGN_FLAG = 'typ-ai-redesign';

/** Query param SingleForm writes and useThankYouData reads for the V2 gate; also the manual override. */
export const TYP_AI_REDESIGN_PARAM = 'ai_redesign';

/**
 * The Feathery field where a lander chooses the AI intake agent: `CX Agent`, `Agentforce` or
 * `Default`. Read by `agentFromField` (utils/typAgent.ts).
 */
export const TYP_AI_AGENT_FIELD = 'ai_agent';

/**
 * Carries the chosen agent from the lander to /thank-you as a number (`PLATFORM_OF` in
 * utils/typAgent.ts), so the URL does not name our AI tools. On the lander it overrides the
 * `ai_agent` field. A chosen agent opens the V2 gate and replaces the three option flags above.
 */
export const TYP_PLATFORM_PARAM = 'platform';

/** In-page intake form experiment. Multivariate — compare against INTAKE_TYP_VARIANT, not a boolean. */
export const INTAKE_TYP_FLAG = 'intake-typ';

/** The INTAKE_TYP_FLAG variant that opts a visit into the direct intake-form redirect. */
export const INTAKE_TYP_VARIANT = 'typIntakeForm';

/** StatusPill visibility. Legacy camelCase key — kept as-is because it exists in PostHog. */
export const STATUS_BAR_FLAG = 'showStatusBar';

/**
 * Query param and PostHog person property for the per-user QA flag override.
 * Unlike `?qa=true` the redirect still runs: being served the variant is the point.
 */
export const FLAG_TESTER = 'flag_tester';
